import { useMemo, useSyncExternalStore } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LuChevronRight, LuMenu, LuSearch } from 'react-icons/lu'
import { useMenu } from '../../auth/PermissionContext'
import { MenuIcon } from '../../auth/iconMap'
import { resolveMenuLabel } from '../../auth/resolveMenuLabel'
import { activityStore } from '../../lib/activity'
import { accentFor, fonts, mineral } from '../../theme'
import { canOpen, findActive, flattenLeaves, pushRecent } from './nav'
import ActivityBar from './ActivityBar'
import UserMenu from './UserMenu'

/** Sub-páginas que no están en el menú pero merecen su miga (heredado del Breadcrumb anterior). */
function extraCrumb(path: string): string {
  let extra = ''
  if (path.includes('/programaciones/') && (path.includes('/detalle') || path.includes('/details/'))) {
    extra = sessionStorage.getItem('current_schedule_code') || 'Detalle'
  }
  if (path.includes('/visita')) extra = extra ? `${extra} › Visita` : 'Visita de Campo'
  return extra
}

export default function Topbar({ onOpenMenu, onOpenSearch }: { onOpenMenu: () => void; onOpenSearch: () => void }) {
  const menu = useMenu()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const leaves = useMemo(() => flattenLeaves(menu), [menu])
  const active = findActive(leaves, pathname)
  const acc = accentFor(active?.section.code)
  const activity = useSyncExternalStore(activityStore.subscribe, activityStore.getSnapshot)
  const extra = extraCrumb(pathname)
  const isSubPage = !!active?.item.route && pathname !== active.item.route

  // Sección y grupo también son migas navegables: llevan a su primera pantalla permitida.
  const firstRoute = (pred: (l: (typeof leaves)[number]) => boolean) =>
    leaves.find(l => pred(l) && canOpen(l.item))?.item.route ?? null
  const sectionRoute = active ? firstRoute(l => l.section.id === active.section.id) : null
  const groupRoute = active?.group ? firstRoute(l => l.group?.id === active.group!.id) : null
  const go = (route: string | null) => {
    if (!route || route === pathname) return
    pushRecent(route)
    navigate(route)
  }
  const crumbLink = (route: string | null) => ({
    as: 'button' as const,
    cursor: route && route !== pathname ? 'pointer' : 'default',
    _hover: route && route !== pathname ? { color: acc.deep, textDecoration: 'underline', textUnderlineOffset: '3px' } : {},
    onClick: () => go(route),
    transition: 'color .15s',
  })

  return (
    <Box as="header" position="sticky" top={0} zIndex={40} h="64px"
      bg="rgba(255, 255, 255, 0.86)" backdropFilter="saturate(1.4) blur(14px)" borderBottom={`1px solid ${mineral.line}`}>
      <Flex h="100%" align="center" gap={3} px={{ base: 4, md: 8 }}>
        <Box as="button" {...{ type: 'button' }} display={{ base: 'flex', lg: 'none' }} p={2} mr={1} borderRadius="10px" color={mineral.ink2}
          _hover={{ bg: mineral.sunk }} onClick={onOpenMenu} aria-label="Abrir menú">
          <LuMenu size={22} />
        </Box>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={active?.item.id ?? pathname} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {active ? (
              <>
                <Flex as="button" {...{ type: 'button', 'aria-label': resolveMenuLabel(active.section.label, t) }} w="30px" h="30px" borderRadius="9px"
                  align="center" justify="center" bg={acc.color} color="#fff" flexShrink={0} cursor="pointer"
                  boxShadow={`0 6px 14px -6px ${acc.color}`} transition="transform .15s" _hover={{ transform: 'translateY(-1px)' }}
                  onClick={() => go(sectionRoute)}>
                  <MenuIcon name={active.section.icon} size={16} />
                </Flex>
                <Text display={{ base: 'none', md: 'block' }} fontSize="13.5px" color={mineral.ink3} whiteSpace="nowrap" {...crumbLink(sectionRoute)}>
                  {resolveMenuLabel(active.section.label, t)}
                </Text>
                {active.group && (
                  <>
                    <Box display={{ base: 'none', xl: 'block' }} color={mineral.ink4}><LuChevronRight size={14} /></Box>
                    <Text display={{ base: 'none', xl: 'block' }} fontSize="13.5px" color={mineral.ink3} whiteSpace="nowrap" {...crumbLink(groupRoute)}>
                      {resolveMenuLabel(active.group.label, t)}
                    </Text>
                  </>
                )}
                <Box display={{ base: 'none', md: 'block' }} color={mineral.ink4}><LuChevronRight size={14} /></Box>
                <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={isSubPage || extra ? mineral.ink2 : mineral.ink} lineClamp={1}
                  cursor={isSubPage ? 'pointer' : 'default'} _hover={isSubPage ? { color: acc.deep } : {}}
                  onClick={() => isSubPage && active.item.route && navigate(active.item.route)}>
                  {resolveMenuLabel(active.item.label, t)}
                </Text>
                {extra && (
                  <>
                    <Box color={mineral.ink4}><LuChevronRight size={14} /></Box>
                    <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={mineral.ink} lineClamp={1}>{extra}</Text>
                  </>
                )}
              </>
            ) : (
              <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={mineral.ink}>Kuntur</Text>
            )}
          </motion.div>
        </AnimatePresence>

        <Box flex={1} />

        <AnimatePresence>
          {activity.writes > 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
              <Flex align="center" gap={2} px={3} h="30px" borderRadius="full" bg={mineral.oroSoft} color={mineral.oroDeep} fontSize="12px" fontWeight={600}>
                <motion.span animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }}
                  style={{ width: 7, height: 7, borderRadius: 7, background: mineral.oro, display: 'inline-block' }} />
                Guardando…
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>

        <Flex as="button" {...{ type: 'button' }} display={{ base: 'flex', lg: 'none' }} p={2} borderRadius="10px" color={mineral.ink2}
          _hover={{ bg: mineral.sunk }} onClick={onOpenSearch} aria-label="Buscar">
          <LuSearch size={20} />
        </Flex>

        <UserMenu />
      </Flex>
      <ActivityBar accent={acc.color} />
    </Box>
  )
}
